import { FormEvent, useState } from 'react'
import { HoldDomain } from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { Stack } from '@/console/ui/Stack'
import { HoldFormError, HoldFormInput, HoldFormKind, fieldOptionsForDomain, validateHoldForm } from './holdForm'

/** 시안 `#s-disp-6` 좌측(규칙 추가) 이식. */
export type HoldCreateFormProps = {
  // LOGISPRD-12506 최종 리뷰 C1 — 두 번째 인자는 "등록 후 목록 재조회로 반영 확인" 체크박스
  // (`autoRecheck`) 상태 그대로다. 호출부(`DispatcherTab`)가 이 값을 `createHoldRule` 의
  // `confirmRegistration` 옵션에 그대로 넘긴다(§4.1 함정 — 등록 200 ≠ 저장 성공).
  onCreate: (input: HoldFormInput, confirmRegistration: boolean) => void
  /** 다른 조치가 진행 중이면 제출 버튼을 잠근다(PR 리뷰 — 눌러도 조용히 버려지던 문제). */
  submitting?: boolean
}

const KIND_OPTIONS: { value: HoldFormKind; label: string }[] = [
  { value: 'attribute', label: '특정 값에 해당하는 주문' },
  { value: 'predicate', label: '여러 조건을 조합한 주문' },
  { value: 'maintenance', label: '도메인 전체 (점검용)' },
]

const DOMAIN_OPTIONS: { value: HoldDomain; label: string }[] = [
  { value: 'fulfillment-dispatcher', label: 'fulfillment-dispatcher' },
  { value: '29cm-dispatcher', label: '29cm-dispatcher' },
  { value: 'fulfillment', label: 'fulfillment' },
  { value: '*', label: '전체' },
]

const CONFIRM_BODY = '이 도메인의 주문 전건이 멈춥니다.'

const toEpochOrNull = (localDateTime: string): number | null => (localDateTime ? new Date(localDateTime).getTime() : null)

const FieldError = ({ message }: { message: string | undefined }): JSX.Element | null =>
  message ? <p style={{ fontSize: 12.5, color: 'var(--crit)', margin: '-3px 0 0' }}>{message}</p> : null

export const HoldCreateForm = ({ onCreate, submitting = false }: HoldCreateFormProps): JSX.Element => {
  const [kind, setKind] = useState<HoldFormKind>('attribute')
  const [domain, setDomain] = useState<HoldDomain>('fulfillment-dispatcher')
  const [field, setField] = useState<string>(fieldOptionsForDomain('fulfillment-dispatcher')[0].value)
  // 코드 리뷰 반영(Finding 3) — 시안은 value= 로 예시 화주코드를 박아 뒀지만, 이 폼은 2단계에서
  // 실제 POST 를 쏜다. 초기값을 비우고 예시는 placeholder 로만 보여준다(오조작으로 실 화주 Hold
  // 가 걸리는 걸 막는다).
  const [valuesRaw, setValuesRaw] = useState('')
  const [holdId, setHoldId] = useState('')
  const [from, setFrom] = useState('')
  const [until, setUntil] = useState('')
  const [autoRecheck, setAutoRecheck] = useState(true)
  const [errors, setErrors] = useState<HoldFormError[]>([])
  const [pendingInput, setPendingInput] = useState<HoldFormInput | null>(null)

  const fieldOptions = fieldOptionsForDomain(domain)
  const errorFor = (name: HoldFormError['field']): string | undefined => errors.find((e) => e.field === name)?.message

  const handleDomainChange = (nextDomain: HoldDomain): void => {
    setDomain(nextDomain)
    const nextOptions = fieldOptionsForDomain(nextDomain)
    if (!nextOptions.some((o) => o.value === field)) {
      setField(nextOptions[0].value)
    }
  }

  const buildInput = (): HoldFormInput => ({
    kind,
    holdId,
    domain,
    field,
    values: valuesRaw,
    from: kind === 'maintenance' ? toEpochOrNull(from) : null,
    until: kind === 'maintenance' ? toEpochOrNull(until) : null,
  })

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    const input = buildInput()
    const nextErrors = validateHoldForm(input)
    setErrors(nextErrors)

    if (nextErrors.length > 0) {
      return
    }

    if (kind === 'maintenance' || domain === '*') {
      setPendingInput(input)
      return
    }

    onCreate(input, autoRecheck)
  }

  const confirmCreate = (): void => {
    if (pendingInput) {
      onCreate(pendingInput, autoRecheck)
    }
    setPendingInput(null)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <Field label="무엇을 멈출까요">
          <select value={kind} onChange={(e) => setKind(e.target.value as HoldFormKind)}>
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="어느 구간에서">
          <select value={domain} onChange={(e) => handleDomainChange(e.target.value as HoldDomain)}>
            {DOMAIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        {kind !== 'maintenance' && (
          <>
            <Field label="기준 항목">
              <select value={field} onChange={(e) => setField(e.target.value)}>
                {fieldOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="값 (전체 교체)">
              <input
                type="text"
                value={valuesRaw}
                placeholder="NIKE, 29CM_NIKE"
                spellCheck={false}
                onChange={(e) => setValuesRaw(e.target.value)}
              />
            </Field>
            <FieldError message={errorFor('values')} />
          </>
        )}

        <Field label="규칙 ID">
          <input type="text" placeholder="wms-patch-0909" value={holdId} onChange={(e) => setHoldId(e.target.value)} />
        </Field>
        <FieldError message={errorFor('holdId')} />

        {kind === 'maintenance' && (
          <>
            <Field label="시작">
              <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="끝 (제외)">
              <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
            </Field>
            <FieldError message={errorFor('period')} />
          </>
        )}

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: 'var(--ink-2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={autoRecheck} onChange={(e) => setAutoRecheck(e.target.checked)} />
          <span>등록 후 목록 재조회로 반영 확인</span>
        </label>

        <div>
          <Button type="submit" disabled={submitting}>
            규칙 추가
          </Button>
        </div>
      </Stack>

      {/*
        PR 리뷰(3회 지적) — 예전 문구는 "fulfillment 구간은 주문 ID 키만 받아요 — 화주 조건은 안
        걸려요" 였는데, 같은 화면의 기준 항목 드롭다운(`FULFILLMENT_FIELDS`)은 화주 코드를 후보로
        준다. 둘 중 하나가 거짓이라 문구를 믿으면 화주 Hold 를 포기하고, 드롭다운을 믿으면 안 먹는
        규칙을 등록하게 된다.

        BE 확인(`FulfillmentRequestItemWorkflowImpl`) — `fulfillment` 도메인은 Hold 를 두 지점에서
        평가하고 넘기는 attributes 가 다르다.
         - Checkpoint 1: `mapOf("orderId" to input.orderId)` — 화주 조건이 아예 없다
         - Checkpoint 2(출고지시 직전): `{ orderId, shipperCode? }` — 단 `shipperCode` 는
           `lookUpAndSaveOption` 이후 `savedDispatchData` 로만 알 수 있어, 미수렴이면 빠진다
        즉 드롭다운이 맞고 문구가 틀렸다. 대신 "언제나 걸린다" 도 사실이 아니라 단계를 명시한다.
      */}
      <Note>
        fulfillment 구간에서 화주 코드는 <b>출고지시 직전 단계에서만</b> 평가돼요. 그 앞 단계에 있는 주문은 주문 ID 로만 걸립니다 — 화주 기준으로
        전부 멈추려면 dispatcher 도메인 규칙을 함께 거세요.
      </Note>

      <ConfirmDialog
        open={pendingInput !== null}
        title="Hold 규칙을 등록할까요?"
        body={CONFIRM_BODY}
        confirmLabel="등록"
        danger
        onConfirm={confirmCreate}
        onCancel={() => setPendingInput(null)}
      />
    </form>
  )
}
